import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiErrorDetail } from "../lib/api";
import { Bike, Mail, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { useForceDarkTheme } from "../lib/theme";

/**
 * Phase 3.83 — Passwort vergessen / Reset anfordern.
 *
 * Generic-response Pattern: zeigt immer dieselbe Bestätigung an, unabhängig
 * davon ob die E-Mail existiert. Im Dev-Modus (kein Resend) liefert der
 * Server zusätzlich `reset_token` zurück; wir zeigen dann direkt einen Reset-
 * Link an, damit der Nutzer den Flow ohne Mail abschließen kann.
 *
 * Unterstützt `?email=` zur Vorbefüllung — genutzt vom „Konto sichern"-Hinweis
 * für frisch angelegte Gast-Konten (1-Klick-Passwort festlegen).
 */
export default function ForgotPasswordPage() {
    useForceDarkTheme();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState(searchParams.get("email") || "");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [devToken, setDevToken] = useState("");
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            setSubmitted(true);
            if (data?.reset_token) {
                setDevToken(data.reset_token);
            }
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 justify-center mb-8">
                    <Logo className="contents" boxClass="w-12 h-12" iconClass="w-7 h-7" deClass="text-black/40" />
                </Link>

                <div className="bg-white border border-gray-200 rounded-sm p-8">
                    {!submitted ? (
                        <>
                            <h1 className="font-display text-3xl font-black tracking-tight mb-1">Passwort zurücksetzen</h1>
                            <p className="text-gray-600 text-sm mb-6">
                                Geben Sie Ihre E-Mail-Adresse ein. Wenn ein Konto existiert, senden wir Ihnen einen Link zum Zurücksetzen.
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                        E-Mail-Adresse
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoComplete="email"
                                        className="mt-1.5 h-11 rounded-sm"
                                        data-testid="forgot-password-email"
                                    />
                                </div>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm" data-testid="forgot-password-error">
                                        {error}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    disabled={loading || !email}
                                    className="w-full h-12 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                                    data-testid="forgot-password-submit"
                                >
                                    {loading ? "Sende…" : "Link anfordern"}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div data-testid="forgot-password-success">
                            <div className="flex justify-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                                </div>
                            </div>
                            <h2 className="font-display text-2xl font-black tracking-tight text-center mb-2">
                                Anleitung versendet
                            </h2>
                            <p className="text-gray-600 text-sm text-center mb-6">
                                Falls die E-Mail-Adresse existiert, wurde eine Anleitung zum Zurücksetzen versendet. Bitte prüfen Sie Ihren Posteingang.
                            </p>

                            {devToken && (
                                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4" data-testid="forgot-password-dev-link">
                                    <div className="flex items-center gap-2 mb-2 text-amber-900 font-bold text-sm">
                                        <Mail className="w-4 h-4" />
                                        Dev-Modus: Direkter Reset-Link
                                    </div>
                                    <p className="text-xs text-amber-900/80 mb-3">
                                        Solange kein Mail-Versand konfiguriert ist, zeigen wir den Link hier direkt an:
                                    </p>
                                    <Link
                                        to={`/reset-password?token=${encodeURIComponent(devToken)}`}
                                        className="block w-full text-center bg-[#111827] hover:bg-black text-white font-bold rounded-sm px-4 py-2.5 text-sm uppercase tracking-wider"
                                        data-testid="forgot-password-dev-link-btn"
                                    >
                                        Jetzt Passwort zurücksetzen
                                    </Link>
                                </div>
                            )}

                            <Link
                                to="/login"
                                className="block w-full text-center border border-gray-300 hover:border-[#111827] text-[#111827] font-bold rounded-sm px-4 py-2.5 text-sm uppercase tracking-wider"
                                data-testid="forgot-password-back-to-login"
                            >
                                Zurück zum Login
                            </Link>
                        </div>
                    )}

                    {!submitted && (
                        <div className="mt-6 pt-6 border-t border-gray-100 text-sm text-center text-gray-600">
                            Doch nicht?{" "}
                            <Link to="/login" className="font-bold underline decoration-2 underline-offset-4 text-[#111827]" data-testid="forgot-password-login-link">
                                Zurück zum Login
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
